import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  StyleProps,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Championnat } from 'features/championnats/championnat.types';
import { BPCCard } from 'common/components/BPCCard';
import { RoutePath } from 'router/routes.paths';
import { useNavigate } from 'react-router-dom';
import {
  deleteChampionnatPersonnalise,
  updateChampionnatPersonnalise,
} from '../championnat.api';
import { useConnectedUserContext } from 'features/user/useConnectedUserContext';
import { useRef } from 'react';

export default function ChampionnatPersonnaliseCard({
  championnat,
  ...styleProps
}: { championnat: Championnat } & StyleProps) {
  const { user } = useConnectedUserContext();
  const navigate = useNavigate();
  const toast = useToast();
  const suppressionDisclosure = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const runAction = async (action: () => Promise<unknown>) => {
    try {
      await action();
      window.location.reload();
    } catch (e: any) {
      toast({
        title: 'Erreur',
        description: e.message,
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <BPCCard
        linkTo={`${RoutePath.championnats}/${championnat.id}`}
        title={championnat.name}
        subTitles={['Championnat personnalisé']}
        avatarProps={{ name: championnat.name }}
        bottomRightContent={
          <Box
            aria-label={
              championnat.isActive
                ? 'Championnat en cours'
                : 'Championnat fermé'
            }
            backgroundColor={championnat.isActive ? 'green.400' : 'gray.400'}
            borderRadius="full"
            boxSize="2"
          />
        }
        menuItems={
          user
            ? [
                {
                  name: 'Modifier',
                  onClick: () =>
                    navigate(
                      `${RoutePath.championnats}/${championnat.id}/edition`,
                    ),
                },
                {
                  name: championnat.isActive
                    ? 'Clore le championnat'
                    : 'Rouvrir le championnat',
                  color: championnat.isActive ? 'red' : 'green',
                  onClick: () =>
                    runAction(() =>
                      updateChampionnatPersonnalise(
                        { ...championnat, isActive: !championnat.isActive },
                        user.id,
                      ),
                    ),
                },
                {
                  name: 'Supprimer',
                  color: 'red',
                  onClick: suppressionDisclosure.onOpen,
                },
              ]
            : undefined
        }
        {...styleProps}
      />
      <AlertDialog
        isOpen={suppressionDisclosure.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={suppressionDisclosure.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer le championnat ?</AlertDialogHeader>
            <AlertDialogBody>
              Le championnat « {championnat.name} » sera définitivement
              supprimé.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={suppressionDisclosure.onClose}>
                Annuler
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => {
                  suppressionDisclosure.onClose();
                  if (user) {
                    runAction(() =>
                      deleteChampionnatPersonnalise(championnat, user.id),
                    );
                  }
                }}
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
